/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import clsx from 'clsx'
import { addDays, format, parseISO } from 'date-fns'
import ja from 'date-fns/locale/ja'
import React from 'react'
import {
  ContractStatusType,
  FacilityAcceptanceStatus,
  FacilityAcceptanceText,
  FacilityAvailableStatusCode,
  FacilityPhoneSupportText,
} from '../entites/FacilityInfo'
import { Icon } from '../utils/Icon'
import { imagePath } from '../utils/ImagePath'
import { color } from '../utils/Style'
import { CircleStatusIcon } from './CircleStatusIcon'
import { Stack } from './layout/Stack'

type CardProps = {
  children: React.ReactNode
}
export const Card = ({ children }: CardProps) => {
  return <div css={cardStyle}>{children}</div>
}

const cardStyle = css`
  overflow: hidden;
  background-color: white;
  border-radius: 10px;
`

type CardHeroProps = {
  imageUrl: string
  isSignedIn: boolean
  bookmarkId: string | null
  id: string
  bookmarkModalShowEnabled: boolean
  handleBookmark: VoidFunction
  handleUnbookmark: VoidFunction
  href: string
  imageStyle?: React.CSSProperties
}

export const CardHero = ({
  imageUrl,
  isSignedIn,
  bookmarkId,
  handleBookmark,
  handleUnbookmark,
  href,
  imageStyle,
}: CardHeroProps) => {
  const bookmarkButton = bookmarkId ? (
    <span
      role="button"
      className="likeButton bookmarkRemove bookmark"
      onClick={handleUnbookmark}
    >
      <Icon
        iconName="map_dialog_like"
        style={{
          width: '26px',
          height: '26px',
          display: 'block',
        }}
      />
    </span>
  ) : (
    <span
      role="button"
      className="likeButton bookmarkAdd bookmark"
      onClick={handleBookmark}
    >
      <Icon
        iconName="map_dialog_like_outline"
        style={{
          width: '26px',
          height: '26px',
          display: 'block',
          color: 'rgba(255, 255, 255, 0)', // これがないと、なぜかmap_dialog_like_outline の内側が黒く塗りつぶされてしまう
        }}
      />
    </span>
  )

  const handleLink = (href: string) => {
    if (href !== '') {
      window.location.href = href
    }
  }

  return (
    <div css={heroStyle}>
      <img
        src={imageUrl || imagePath('map_facility_placeholder.png')}
        alt=""
        onClick={() => handleLink(href)}
        style={imageStyle}
      />
      {isSignedIn && bookmarkButton}
    </div>
  )
}

const heroStyle = css`
  position: relative;
  cursor: pointer;

  & img {
    height: 80px;
    width: 100%;
    object-fit: cover;
    vertical-align: bottom;
  }

  & .likeButton {
    position: absolute;
    right: 0;
    bottom: 0;
    padding: 8px;
  }
`

type CardHeaderProps = {
  name: string
  href: string
  attachedClinicName: string | null
  detailPublish: boolean | null
}
export const CardHeader = ({
  name,
  href,
  attachedClinicName,
  detailPublish,
}: CardHeaderProps) => {
  const isInternalLink = href && href.slice(0, 1) === '/'
  const isBlank = href === ''
  return isBlank ? (
    <a
      css={cardHeaderCss}
      target={isInternalLink ? '' : '_blank'}
      rel={isInternalLink ? '' : 'noopener noreferrer'}
    >
      {attachedClinicName && (
        <div className="attached">{attachedClinicName} 併設</div>
      )}
      <div className="nameDiv">
        <div className="name">{name}</div>
        <span className="nextIcon">
          {detailPublish ? (
            <LinkButton href={href} outlined={true} primary={true}>
              施設詳細
            </LinkButton>
          ) : (
            ''
          )}
        </span>
      </div>
    </a>
  ) : (
    <a
      css={cardHeaderCss}
      href={href}
      target={isInternalLink ? '' : '_blank'}
      rel={isInternalLink ? '' : 'noopener noreferrer'}
    >
      {attachedClinicName && (
        <div className="attached">{attachedClinicName} 併設</div>
      )}
      <div className="nameDiv">
        <div className="name">{name}</div>
        <span className="nextIcon">
          {detailPublish ? (
            <LinkButton href={href} outlined={true} primary={true}>
              施設詳細
            </LinkButton>
          ) : (
            <span css={CardHeaderDetailCss}>施設サイト&gt;</span>
          )}
        </span>
      </div>
    </a>
  )
}

const CardHeaderDetailCss = css`
  font-size: 8px;
  color: #0000ff;
`

const cardHeaderCss = css`
  position: relative;
  padding: 14px 16px 10px;
  line-height: inherit;
  border-bottom: 1px solid rgba(15, 13, 3, 0.1);
  display: block;
  text-decoration: none;
  color: black;
  font-weight: 700;

  & .nameDiv {
    display: flex;
    align-items: center;
  }

  & .attached {
    font-weight: bold;
    font-size: 11px;
    line-height: 13px;
    color: #3f3d35;
    margin-bottom: 5px;
  }
  & .name {
    flex: 1 1 0%;
    font-size: 14px;
    word-break: break-word;
    line-height: 21px;
  }
  & .nextIcon {
    flex-shrink: 0;
    margin-left: 16px;
  }
`
type CardContentProps = {
  todayStatusCode: FacilityAvailableStatusCode
  todayStatusText: string
  tomorrowStatusCode: FacilityAvailableStatusCode
  tomorrowStatusText: string
  distance?: any
  isSignedIn: boolean
  googleMapUrl?: string
  latitude: number
  longitude: number
  openingTimes: string[]
  contractStatus: ContractStatusType
  isVisualizationGroupFacility: boolean
  useReservationEnable: boolean
  bold?: 'today' | 'tomorrow'
}
export const CardContent = ({
  todayStatusCode,
  todayStatusText,
  tomorrowStatusCode,
  tomorrowStatusText,
  distance,
  isSignedIn,
  googleMapUrl,
  latitude,
  longitude,
  openingTimes,
  contractStatus,
  isVisualizationGroupFacility,
  useReservationEnable,
  bold,
}: CardContentProps) => {
  const href =
    googleMapUrl ||
    `https://www.google.com/maps?q=${latitude},${longitude}&z=18&t=m&hl=ja&gl=US&mapclient=apiv3`

  const ua = navigator.userAgent
  const isiPhone = ua.includes('iPhone')
  const iPhoneOverrideStyle = isiPhone
    ? css`
        letter-spacing: 1.1px !important;
      `
    : ''

  const today = new Date()
  const tomorrow = addDays(today, 1)
  const todayDate = format(today, 'MM/dd', {
    locale: ja,
  })
  const todayWeekDay = format(today, 'eee', {
    locale: ja,
  })
  const tomorrowDate = format(tomorrow, 'MM/dd', {
    locale: ja,
  })
  const tomorrowWeekDay = format(tomorrow, 'eee', {
    locale: ja,
  })

  return (
    <div css={contentStyle}>
      {((contractStatus === 'contracted' && useReservationEnable) ||
        isVisualizationGroupFacility) && (
        <React.Fragment>
          <div className="dateGrid">
            <span
              className={clsx('date', bold === 'today' && 'bold')}
              css={iPhoneOverrideStyle}
            >
              今日
            </span>
            <span
              className={clsx('date', bold === 'today' && 'bold')}
              css={iPhoneOverrideStyle}
            >
              {todayDate}
              <span className="weekday">（{todayWeekDay}）</span>
            </span>
            <Stack horizontal align="center" gap="6px">
              <CircleStatusIcon
                statusCode={todayStatusCode}
                size="sm"
                style={{
                  marginLeft:
                    todayStatusCode === 'congestion' ||
                    todayStatusCode === 'full'
                      ? '-1px'
                      : undefined,
                }}
              />
              <span css={statusTextStyle} className={todayStatusCode}>
                {todayStatusText}
              </span>
            </Stack>
            <span
              className={clsx('date', bold === 'tomorrow' && 'bold')}
              css={iPhoneOverrideStyle}
            >
              明日
            </span>
            <span
              className={clsx('date', bold === 'tomorrow' && 'bold')}
              css={iPhoneOverrideStyle}
            >
              {tomorrowDate}
              <span className="weekday">（{tomorrowWeekDay}）</span>
            </span>
            <Stack horizontal align="center" gap="6px">
              <CircleStatusIcon
                statusCode={tomorrowStatusCode}
                size="sm"
                style={{
                  marginLeft:
                    tomorrowStatusCode === 'congestion' ||
                    tomorrowStatusCode === 'full'
                      ? '-1px'
                      : undefined,
                }}
              />
              <span css={statusTextStyle} className={tomorrowStatusCode}>
                {tomorrowStatusText}
              </span>
            </Stack>
          </div>
        </React.Fragment>
      )}
      <div
        css={css`
          display: ${openingTimes.length > 0 ? 'grid' : 'none'};
          grid-template-columns: auto 1fr;
          gap: 6px;
        `}
      >
        <Icon
          iconName={'map_dialog_calendar'}
          style={{
            width: '14px',
            height: '14px',
            transform: 'translateY(4px)',
          }}
        />
        <div
          css={css`
            line-height: 160%;
          `}
        >
          {openingTimes.map((item, i) => {
            const slash = i === openingTimes.length - 1 ? '' : '／'
            return (
              <span
                key={i}
                css={css`
                  display: inline-block;
                `}
              >
                {item} {slash}
              </span>
            )
          })}
        </div>
      </div>
      <Stack horizontal align="center" gap="6px">
        <Icon
          iconName={'location_on'}
          style={{ width: '14px', height: '14px' }}
        />
        <span>{getDistanceText(distance, isSignedIn)}</span>
        <a
          className="mapLink"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          地図アプリで見る
        </a>
      </Stack>
    </div>
  )
}

const contentStyle = css`
  display: grid;
  align-items: center;
  padding: 14px 16px 12px;
  font-size: 12px;
  line-height: 1.5rem;

  & .dateGrid {
    display: grid;
    grid-template-columns: auto auto auto;
    column-gap: 2px;
    justify-content: left;
    margin-bottom: 4px;
    & > * {
      height: 24px;
    }
  }

  & .date {
    font-weight: 400;
    text-align: justify;
    text-justify: inter-character;
    letter-spacing: 1.1px;

    &.bold {
      font-weight: 700;
      letter-spacing: inherit;
    }

    & .weekday {
      font-size: 9px;
    }
  }

  & .mapLink {
    background-color: #f6f5f4;
    border-radius: 50px;
    padding: 5px 10px 6px;
    font-size: 10px;
    line-height: 1;
    text-decoration: none;
    color: #7f7d76;
  }
`

const statusTextStyle = css`
  font-weight: bold;
  &.vacancies {
    color: ${color.colorVacancies};
  }
  &.vacancies_little {
    color: ${color.colorVacanciesLittle};
  }
  &.congestion {
    color: ${color.colorFull};
  }
  &.full {
    color: ${color.colorFull};
  }
`

type CardFooterContractedProps = {
  id: string
  showPath?: string
  acceptanceTimeText: FacilityAcceptanceText | null
  acceptancePhoneTimeText: FacilityPhoneSupportText | null
  acceptanceStatus: FacilityAcceptanceStatus
  phoneNumber: string | null
  isVisualizationGroupFacility: boolean
  useRegistrationEnable: boolean
  useReservationEnable: boolean
  useReservationStartDate: string | null
  acceptancePhoneStatus: FacilityAcceptanceStatus
}
export const CardFooterContracted = ({
  id,
  showPath,
  acceptanceTimeText,
  acceptancePhoneTimeText,
  acceptanceStatus,
  phoneNumber,
  isVisualizationGroupFacility,
  useRegistrationEnable,
  useReservationEnable,
  useReservationStartDate,
  acceptancePhoneStatus,
}: CardFooterContractedProps) => {
  const isSameNotContract = () => {
    return (
      !useRegistrationEnable &&
      !useReservationEnable &&
      !isVisualizationGroupFacility
    )
  }
  return (
    <div css={footerStyle}>
      {!isSameNotContract() &&
        !useRegistrationEnable &&
        !(!useReservationEnable && isVisualizationGroupFacility) && (
          <p className="availableTimeText">
            現在、施設への登録の受付を停止しています
          </p>
        )}
      {!isSameNotContract() &&
        !useReservationEnable &&
        !isVisualizationGroupFacility &&
        useReservationStartDate && (
          <p className="availableTimeText">
            {`${format(parseISO(useReservationStartDate), 'M月d日（eee）H時', {
              locale: ja,
            })}${
              format(parseISO(useReservationStartDate), 'm', {
                locale: ja,
              }) === '0'
                ? ''
                : format(parseISO(useReservationStartDate), 'm', {
                    locale: ja,
                  }) + '分'
            }から受付開始`}
          </p>
        )}

      {acceptanceStatus !== 'acceptance' &&
        useReservationEnable &&
        !!acceptanceTimeText &&
        acceptanceTimeText.date !== null && (
          <p className="availableTimeText">
            {acceptanceTimeText.date}
            <b>{acceptanceTimeText.time}</b>
            {acceptanceTimeText.suffix}
          </p>
        )}
      {!useReservationEnable &&
        isVisualizationGroupFacility &&
        !!acceptancePhoneTimeText && (
          <p className="availableTimeText phoneSupportText">
            {acceptancePhoneTimeText.label}
            <b>{acceptancePhoneTimeText.emphasis}</b>
          </p>
        )}
      {!isSameNotContract() &&
        (useRegistrationEnable ||
          useReservationEnable ||
          !isVisualizationGroupFacility) && (
          <div>
            <div className="buttons">
              {(!isVisualizationGroupFacility ||
                (isVisualizationGroupFacility && useReservationEnable)) && (
                <LinkButton
                  href={`${showPath}?transition_source_path=/map_facilities`}
                  disabled={!useReservationEnable}
                >
                  予約する
                </LinkButton>
              )}
              {isVisualizationGroupFacility &&
                useReservationEnable === false && (
                  <LinkButton
                    href={`tel:${phoneNumber}`}
                    outlined={acceptancePhoneStatus === 'acceptance'}
                    primary={acceptancePhoneStatus === 'acceptance'}
                    disabled={acceptancePhoneStatus !== 'acceptance'}
                  >
                    電話する
                  </LinkButton>
                )}
            </div>
            {useRegistrationEnable && (
              <p className="availableBottomPaddingTimeText">
                ご予約の前に子どもごとに
                <a
                  href={`user_child_facilities?facility_id=${id}&transition_source_path=/map_facilities`}
                  className="availableBottomLinkText"
                >
                  施設への登録
                </a>
                が必要です。
              </p>
            )}
          </div>
        )}
      {!useRegistrationEnable &&
        !useReservationEnable &&
        isVisualizationGroupFacility && (
          <div className="buttons">
            <LinkButton
              href={`tel:${phoneNumber}`}
              outlined={acceptancePhoneStatus === 'acceptance'}
              primary={acceptancePhoneStatus === 'acceptance'}
              disabled={acceptancePhoneStatus !== 'acceptance'}
            >
              電話する
            </LinkButton>
          </div>
        )}
      {isSameNotContract() && (
        <div className="buttons">
          <LinkButton
            href={`tel:${phoneNumber}`}
            outlined={true}
            primary={true}
          >
            電話する
          </LinkButton>
        </div>
      )}
    </div>
  )
}

type CardFooterNotContractedProps = {
  acceptancePhoneTimeText: FacilityPhoneSupportText | null
  acceptanceStatus: FacilityAcceptanceStatus
  phoneNumber: string | null
  isVisualizationGroupFacility: boolean
}

export const CardFooterNotContracted = ({
  phoneNumber,
  acceptancePhoneTimeText,
  acceptanceStatus,
  isVisualizationGroupFacility,
}: CardFooterNotContractedProps) => {
  return (
    <div css={footerStyle}>
      {isVisualizationGroupFacility && !!acceptancePhoneTimeText && (
        <p className="availableTimeText phoneSupportText">
          {acceptancePhoneTimeText.label}
          <b>{acceptancePhoneTimeText.emphasis}</b>
        </p>
      )}
      <div className="buttons">
        <LinkButton
          href={`tel:${phoneNumber}`}
          outlined={acceptanceStatus === 'acceptance'}
          primary={acceptanceStatus === 'acceptance'}
          disabled={acceptanceStatus !== 'acceptance'}
        >
          電話する
        </LinkButton>
      </div>
    </div>
  )
}

const footerStyle = css`
  border-top: 1px solid rgba(15, 13, 3, 0.1);

  .availableTimeText {
    color: ${color.colorUltraDarkGray};
    font-size: 14px;
    margin-top: 15px;
    text-align: center;
  }
  /* .availableTimeText b にすると通常施設の予約受付表記まで大きくなる */
  .phoneSupportText b {
    font-size: 16px;
    margin-left: 0.4em;
  }
  .availableBottomPaddingTimeText {
    color: ${color.colorUltraDarkGray};
    font-size: 14px;
    text-align: center;
    margin-bottom: 15px;
  }
  .availableBottomLinkText {
    color: ${color.colorBlue};
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    padding: 15px 20px;
  }
`

type LinkButtonProps = {
  children: React.ReactNode
  href: string
  outlined?: boolean
  disabled?: boolean
  primary?: boolean
}
const LinkButton = ({
  children,
  href,
  outlined = false,
  disabled = false,
  primary = true,
}: LinkButtonProps) => {
  const buttonClassNames: string[] = ['Button', '-full', '-rounded']
  const labelClassNames: string[] = ['Button__label']
  if (outlined) buttonClassNames.push('-outlined')
  if (disabled) {
    buttonClassNames.push('-color-gray10-white-text')
  } else if (primary) {
    buttonClassNames.push('-color-primary')
  }

  return (
    <a
      className={buttonClassNames.join(' ')}
      href={href}
      css={disabled ? linkButtonDisableStyle : linkButtonStyle}
    >
      <span className="Button__inner">
        <span
          className={labelClassNames.join(' ')}
          css={css`
            line-height: 1;
          `}
        >
          {children}
        </span>
      </span>
    </a>
  )
}

const linkButtonStyle = css`
  & > span {
    height: 28px !important;
    font-size: 12px;
  }
`

const linkButtonDisableStyle = css`
  pointer-events: none;
  & > span {
    height: 28px !important;
    font-size: 12px;
    color: #fff;
  }
`

const getDistanceText = (distance: any, isSignedIn: boolean) => {
  if (isSignedIn === true && distance !== null) {
    return `自宅から${distance}km`
  } else if (isSignedIn === false && distance !== null) {
    return `現在位置から${distance}km`
  } else {
    return ``
  }
}
